import { useEffect } from "react";

/**
 * Lightweight, dependency-free confirm modal.
 * Open it by passing a state object: { title, message, confirmLabel, danger, onConfirm }
 * Pass `open={null}` to close.
 */
export default function ConfirmModal({ open, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  if (!open) return null;
  const { title, message, confirmLabel = "Confirm", danger = false, onConfirm } = open;

  const handleConfirm = async () => {
    try {
      await onConfirm?.();
    } finally {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/40 p-4"
      data-testid="confirm-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-md w-full border border-[#E8E1D5] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
        <h3 className="font-serif-villa text-2xl">{title}</h3>
        {message && <p className="text-sm text-[#7A7A7A] mt-2 whitespace-pre-line">{message}</p>}
        <div className="mt-5 flex items-center gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]"
            data-testid="confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            data-testid="confirm-ok"
            className={`px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 ${
              danger
                ? "bg-[#A84C30] text-[#FDFBF7] hover:bg-[#8E3F28]"
                : "villa-btn-primary"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
